// src/App.js
import React, {useState, useEffect} from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {AuthProvider, useAuth} from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import axios from 'axios';

// Install Page
import Install from './pages/Install/Install';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Unauthorized from './pages/auth/Unauthorized';

// Dashboard
import Dashboard from './pages/Dashboard/Dashboard';

// Inventory
import Inventory from './pages/Inventory/Inventory';
import InventoryTransactions from './pages/Inventory/InventoryTransactions';

// Products
import Products from './pages/Products/Products';
import ProductDetails from './pages/Products/ProductDetails';
import ProductForm from './pages/Products/ProductForm';

// Orders
import Orders from './pages/Orders/Orders';
import OrderDetails from './pages/Orders/OrderDetails';
import OrderForm from './pages/Orders/OrderForm';

// Production
import Production from './pages/Production/Production';
import ProductionBatchDetails from './pages/Production/ProductionBatchDetails';
import ProductionBatchForm from './pages/Production/ProductionBatchForm';

// Packing Slips
import PackingSlipsList from './pages/PackingSlips/PackingSlipsList';
import PackingSlipDetail from './pages/PackingSlips/PackingSlipDetail';
import BulkPrintPackingSlips from './pages/PackingSlips/BulkPrintPackingSlips';

// Part Orders
import PartOrders from './pages/PartOrders/PartOrders';
import PartOrderDetails from './pages/PartOrders/PartOrderDetails';
import PartOrderForm from './pages/PartOrders/PartOrderForm';

// Reports
import Reports from './pages/Reports/Reports';

// Users
import Users from './pages/Users/Users';
import UserDetails from './pages/Users/UserDetails';
import UserForm from './pages/Users/UserForm';

// Settings
import Settings from './pages/Settings/Settings';
import Profile from './pages/Settings/Profile';

// Import
import ImportData from './pages/Import/ImportData';
import PartTypes from "./pages/PartTypes/PartTypes";
import StockInventory from "./pages/StockInventory/StockInventory";
import StockInventoryDetail from "./pages/StockInventory/StockInventoryDetail";
import Vendors from "./pages/Vendors/Vendors";
import CutSheetViewer from "./pages/PartOrders/CutSheetViewer";
import SupplyOrder from "./pages/Supplies/SupplyOrder";
import SupplyForecast from "./pages/Supplies/SupplyForecast";
import Customers from "./pages/Customers/Customers";
import CustomerAnalytics from "./pages/Customers/CustomerAnalytics";
import CustomerEdit from "./pages/Customers/CustomerEdit";
import CustomerOrders from "./pages/Customers/CustomerOrders";

function AppContent() {
    const { loading, isInstalled } = useAuth();

    useEffect(() => {
        console.log("Is Installed", isInstalled);
    }, [isInstalled]);

    useEffect(() => {
        console.log('API URL:', process.env.REACT_APP_API_URL);
    }, []);

    if (loading) {
        return <div className="flex items-center justify-center h-screen">Loading...</div>;
    }

    return (
        <>
            <ToastContainer position="top-right" autoClose={5000}/>
            <Routes>
                {/* Installation Route - Only accessible when no users exist */}
                {!isInstalled && <Route path="/install" element={<Install/>}/>}

                {/* Public Routes */}
                <Route path="/login" element={<Login/>}/>
                <Route path="/register" element={<Register/>}/>
                <Route path="/unauthorized" element={<Unauthorized/>}/>

                {/* Redirect root based on installation status */}
                <Route path="/" element={
                    isInstalled
                    ? <Navigate to="/dashboard" replace/>
                    : <Navigate to="/install" replace/>
                }/>

                {/* Protected Routes with Layout */}
                <Route element={<ProtectedRoute/>}>
                    <Route element={<MainLayout/>}>
                        {/* Dashboard - All authenticated users */}
                        <Route path="/dashboard" element={<Dashboard/>}/>

                        {/* Inventory - All authenticated users for viewing */}
                        <Route path="/inventory" element={<Inventory/>}/>
                        <Route path="/inventory/transactions" element={<InventoryTransactions/>}/>

                        {/* Products - Admin and Procurement for full access, Production for read-only */}
                        <Route path="/products" element={<Products/>}/>
                        <Route path="/products/:id" element={<ProductDetails/>}/>
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'procurement']}/>}>
                            <Route path="/products/new" element={<ProductForm/>}/>
                            <Route path="/products/:id/edit" element={<ProductForm/>}/>
                        </Route>

                        <Route element={<ProtectedRoute allowedRoles={['admin', 'procurement', 'production']}/>}>
                            <Route path="/stock" element={<StockInventory />}/>
                            <Route path="/stock/:id" element={<StockInventoryDetail />} />
                        </Route>

                        {/* Part Types - Admin, Procurement */}
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'procurement']}/>}>
                            <Route path="/part-types" element={<PartTypes/>}/>
                        </Route>

                        {/* Orders - Admin for management, Production for fulfillability */}
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'production']}/>}>
                            <Route path="/orders" element={<Orders/>}/>
                            <Route path="/orders/:id" element={<OrderDetails/>}/>
                            <Route element={<ProtectedRoute allowedRoles={['admin']}/>}>
                                <Route path="/orders/new" element={<OrderForm/>}/>
                                <Route path="/orders/:id/edit" element={<OrderForm/>}/>
                            </Route>
                        </Route>

                        {/* Production - Admin and Production */}
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'production']}/>}>
                            <Route path="/production" element={<Production/>}/>
                            <Route path="/production/:id" element={<ProductionBatchDetails/>}/>
                            <Route path="/production/new" element={<ProductionBatchForm/>}/>
                            <Route path="/production/:id/edit" element={<ProductionBatchForm/>}/>
                        </Route>

                        {/* Packing Slips - Admin and Production */}
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'production']}/>}>
                            <Route path="/packing-slips" element={<PackingSlipsList/>}/>
                            <Route path="/packing-slips/print-bulk" element={<BulkPrintPackingSlips/>}/>
                            <Route path="/packing-slips/:id" element={<PackingSlipDetail/>}/>
                        </Route>

                        {/* Part Orders - Admin and Procurement */}
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'procurement']}/>}>
                            <Route path="/part-orders" element={<PartOrders/>}/>
                            <Route path="/part-orders/:id" element={<PartOrderDetails/>}/>
                            <Route path="/part-orders/new" element={<PartOrderForm/>}/>
                            <Route path="/part-orders/edit/:id" element={<PartOrderForm/>}/>
                            <Route path="/part-orders/cut-sheet" element={<CutSheetViewer/>}/>
                            <Route path="/supply-orders" element={<SupplyOrder />} />
                            <Route path="/supply-forecast" element={<SupplyForecast />} />
                        </Route>

                        <Route element={<ProtectedRoute allowedRoles={['admin', 'procurement']}/>}>
                            <Route path="/customers" element={<Customers />}/>
                            <Route path="/customers/analytics" element={<Navigate to="/customers" replace/>}/> {/* Redirect generic analytics to customers list */}
                            <Route path="/customers/:customerId/analytics" element={<CustomerAnalytics />}/>
                            <Route path="/customers/:customerId/orders" element={<CustomerOrders />}/>
                            <Route path="/customers/:customerId/edit" element={<CustomerEdit />}/>
                        </Route>

                        {/* Vendors - Admin, Procurement */}
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'procurement']}/>}>
                            <Route path="/vendors" element={<Vendors/>}/>
                        </Route>

                        {/* Reports - Admin and Procurement */}
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'procurement']}/>}>
                            <Route path="/reports" element={<Reports/>}/>
                        </Route>

                        {/* Users - Admin only */}
                        <Route element={<ProtectedRoute allowedRoles={['admin']}/>}>
                            <Route path="/users" element={<Users/>}/>
                            <Route path="/users/:id" element={<UserDetails/>}/>
                            <Route path="/users/new" element={<UserForm/>}/>
                            <Route path="/users/edit/:id" element={<UserForm/>}/>
                        </Route>

                        {/* Settings and Profile - All authenticated users */}
                        <Route path="/settings" element={<Settings/>}/>
                        <Route path="/profile" element={<Profile/>}/>

                        {/* Import - Admin and Procurement */}
                        <Route element={<ProtectedRoute allowedRoles={['admin', 'procurement']}/>}>
                            <Route path="/import" element={<ImportData/>}/>
                        </Route>
                    </Route>
                </Route>

                {/* Catch all - redirect based on installation status */}
                <Route path="*" element={
                    isInstalled
                    ? <Navigate to="/dashboard" replace/>
                    : <Navigate to="/install" replace/>
                }/>
            </Routes>
        </>
    );
}

function App() {
    return (
        <Router>
            <AuthProvider>
                <AppContent/>
            </AuthProvider>
        </Router>
    );
}

export default App;
